import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {baseUrl, userName} from "../Univ.js";
import "./IoTStyles.css";
function IoTDeviceMapping() {
    // ============================================================
    // STATE
    // ============================================================
    const [devices, setDevices] = useState([]);
    const [lands, setLands] = useState([]);
    const [landCrops, setLandCrops] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState("");
    const [selectedLand, setSelectedLand] = useState("");
    const [selectedLandCrop, setSelectedLandCrop] = useState("");
    const [searchLand, setSearchLand] = useState("");
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingLands, setLoadingLands] = useState(false);
    const [loadingLandCrops, setLoadingLandCrops] = useState(false);
    const [mapping, setMapping] = useState(false);
    // ============================================================
    // LOAD DEVICES + FARMLANDS
    // ============================================================
    useEffect(() => {
        fetchDevices();
        fetchLands();
    }, []);
    // ============================================================
    // FETCH DEVICES
    // ============================================================
    const fetchDevices = async () => {
        try {
            setLoadingDevices(true);
            const response = await axios.get(baseUrl + "iot-devices/");
            console.log("AGRI IoT DEVICES:", response.data);
            if (Array.isArray(response.data)) {
                setDevices(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setDevices(response.data.results);
            } else {
                setDevices([]);
            }
        } catch (error) {
            console.log("DEVICE LOAD ERROR:", error);
            Swal.fire("Error", "Unable to load IoT devices.", "error");
        } finally {
            setLoadingDevices(false);
        }
    };
    // ============================================================
    // FETCH FARMLANDS
    // ============================================================
    const fetchLands = async () => {
        try {
            setLoadingLands(true);
            let url = baseUrl + "lands/";
            if (searchLand.trim() !== "") {
                url += "?search=" + encodeURIComponent(searchLand);
            }
            const response = await axios.get(url);
            console.log("FARMLANDS:", response.data);
            if (Array.isArray(response.data)) {
                setLands(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setLands(response.data.results);
            } else {
                setLands([]);
            }
        } catch (error) {
            console.log("FARMLAND LOAD ERROR:", error);
            Swal.fire("Error", "Unable to load farmlands.", "error");
        } finally {
            setLoadingLands(false);
        }
    };
    // ============================================================
    // SEARCH FARMLANDS
    // ============================================================
    const searchFarmlands = () => {
        fetchLands();
    };
    // ============================================================
    // SELECT FARMLAND
    // ============================================================
    const handleLandChange = async (event) => {
        const landId = event.target.value;
        setSelectedLand(landId);
        setSelectedLandCrop("");
        setLandCrops([]);
        if (!landId) {
            return;
        }
        fetchLandCrops(landId);
    };
    // ============================================================
    // FETCH LAND CROPS
    // ============================================================
    const fetchLandCrops = async (landId) => {
        try {
            setLoadingLandCrops(true);
            const response = await axios.get(baseUrl + "land-crops/?land=" + encodeURIComponent(landId));
            console.log("LAND CROPS:", response.data);
            if (Array.isArray(response.data)) {
                setLandCrops(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setLandCrops(response.data.results);
            } else {
                setLandCrops([]);
            }
        } catch (error) {
            console.log("LAND CROP LOAD ERROR:", error);
            Swal.fire("Error", "Unable to load crops for this farmland.", "error");
        } finally {
            setLoadingLandCrops(false);
        }
    };
    // ============================================================
    // SELECTED DEVICE
    // ============================================================
    const selectedDeviceObject = devices.find(item => String(item.id) === String(selectedDevice));
    // ============================================================
    // SELECTED FARMLAND
    // ============================================================
    const selectedLandObject = lands.find(item => String(item.id) === String(selectedLand));
    // ============================================================
    // SELECTED LAND CROP
    // ============================================================
    const selectedLandCropObject = landCrops.find(item => String(item.id) === String(selectedLandCrop));
    // ============================================================
    // FARMLAND DISPLAY
    // ============================================================
    const getLandName = (land) => {
        if (!land) {
            return "-";
        }
        return (land.name || land.land_name || land.land_id || land.id || "-");
    };
    // ============================================================
    // LAND CROP DISPLAY
    // ============================================================
    const getLandCropName = (landCrop) => {
        if (!landCrop) {
            return "-";
        }
        // --------------------------------------------------------
        // Crop nested object
        // --------------------------------------------------------
        if (landCrop.crop && typeof landCrop.crop === "object") {
            return (landCrop.crop.name || landCrop.crop.crop_name || landCrop.crop.id || "-");
        }
        // --------------------------------------------------------
        // Crop ID / name
        // --------------------------------------------------------
        return (landCrop.crop_name || landCrop.crop || landCrop.name || landCrop.id || "-");
    };
    // ============================================================
    // MAP DEVICE
    // ============================================================
    const mapDevice = async () => {
        if (!selectedDevice) {
            Swal.fire("Required", "Please select an IoT device.", "warning");
            return;
        }
        if (!selectedLand) {
            Swal.fire("Required", "Please select a farmland.", "warning");
            return;
        }
        if (!selectedLandCrop) {
            Swal.fire("Required", "Please select a crop.", "warning");
            return;
        }
        try {
            setMapping(true);
            const payload = {
                device: Number(selectedDevice),
                land: Number(selectedLand),
                land_crop: Number(selectedLandCrop),
                mapped_by: userName || "SYSTEM",
                active: true
            };
            console.log("AGRI IoT MAPPING PAYLOAD:", payload);
            const response = await axios.post(baseUrl + "iot-device-mapping/", payload);
            console.log("MAPPING RESPONSE:", response.data);
            if (response.status === 200 || response.status === 201) {
                Swal.fire({
                    title: "Device Mapped Successfully",
                    text: "IoT device is now connected to the selected farmland and crop.",
                    icon: "success",
                    timer: 2500,
                    showConfirmButton: false
                });
                // ------------------------------------------------
                // RESET
                // ------------------------------------------------
                setSelectedDevice("");
                setSelectedLand("");
                setSelectedLandCrop("");
                setLandCrops([]);
                setSearchLand("");
                fetchDevices();
            }
        } catch (error) {
            console.log("MAPPING ERROR:", error);
            let message = "Unable to map IoT device to farmland.";
            if (error.response && error.response.data) {
                console.log("SERVER RESPONSE:", error.response.data);
                if (error.response.data.detail) {
                    message = error.response.data.detail;
                } else {
                    message = JSON.stringify(error.response.data);
                }
            }
            Swal.fire("Mapping Error", message, "error");
        } finally {
            setMapping(false);
        }
    };
    // ============================================================
    // CLEAR
    // ============================================================
    const clearMapping = () => {
        setSelectedDevice("");
        setSelectedLand("");
        setSelectedLandCrop("");
        setLandCrops([]);
        setSearchLand("");
    };
    // ============================================================
    // UI
    // ============================================================
    return (<section className="container mt-4">


            {/* ==================================================
                HEADER
            ================================================== */}

            <div className="iot-page-header">

                <div>

                    <h3>
                        IoT Device → Farmland Mapping
                    </h3>

                    <p>
                        Connect an agricultural IoT device
                        to a farmland and its active crop
                    </p>

                </div>

            </div>


            {/* ==================================================
                STEP 1 — DEVICE
            ================================================== */}

            <div className="card shadow-sm mb-4">

                <div className="card-header">

                    <strong>
                        1. Select IoT Device
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row">


                        {/* DEVICE */}

                        <div className="col-md-8">

                            <label className="form-label">

                                IoT Device *

                            </label>


                            <select

                                className="form-control"

                                value={selectedDevice}

                                onChange={e => setSelectedDevice(e.target.value)}

                                disabled={loadingDevices}

                            >

                                <option value="">

                                    {loadingDevices ? "Loading devices..." : "Select IoT Device"}

                                </option>


                                {devices.map(device => (<option

                                        key={device.id}

                                        value={device.id}

                                    >

                                        {device.device_id}

                                        {" - "}

                                        {device.device_name}

                                        {" - "}

                                        {device.device_type}

                                    </option>))}

                            </select>

                        </div>


                        {/* STATUS */}

                        <div className="col-md-4">

                            <label className="form-label">

                                Device Status

                            </label>


                            <div className="form-control bg-light">

                                {selectedDeviceObject ? (selectedDeviceObject.is_online ? (
                                        <span className="text-success">

                                                    ● ONLINE

                                                </span>) : (<span className="text-danger">

                                                    ● OFFLINE

                                                </span>)) : (<span className="text-muted">

                                            Select device

                                        </span>)}

                            </div>

                        </div>

                    </div>


                    {/* DEVICE DETAILS */}

                    {selectedDeviceObject && (<div className="row mt-3">


                            <div className="col-md-3">

                                <small className="text-muted">

                                    Device ID

                                </small>

                                <div>

                                    {selectedDeviceObject.device_id}

                                </div>

                            </div>


                            <div className="col-md-3">

                                <small className="text-muted">

                                    Manufacturer

                                </small>

                                <div>

                                    {selectedDeviceObject.manufacturer || "-"}

                                </div>

                            </div>


                            <div className="col-md-3">

                                <small className="text-muted">

                                    Model

                                </small>

                                <div>

                                    {selectedDeviceObject.model_number || "-"}

                                </div>

                            </div>


                            <div className="col-md-3">

                                <small className="text-muted">

                                    Protocol

                                </small>

                                <div>

                                    {selectedDeviceObject.protocol || "-"}

                                </div>

                            </div>

                        </div>)}

                </div>

            </div>


            {/* ==================================================
                STEP 2 — FARMLAND
            ================================================== */}

            <div className="card shadow-sm mb-4">

                <div className="card-header">

                    <strong>
                        2. Select Farmland
                    </strong>

                </div>


                <div className="card-body">


                    {/* SEARCH */}

                    <div className="row mb-3">

                        <div className="col-md-9">

                            <input

                                type="text"

                                className="form-control"

                                placeholder={"Search Farmland / Land ID..."}

                                value={searchLand}

                                onChange={e => setSearchLand(e.target.value)}

                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        searchFarmlands();
                                    }
                                }}

                            />

                        </div>


                        <div className="col-md-3">

                            <button

                                type="button"

                                className="btn btn-primary w-100"

                                onClick={searchFarmlands}

                                disabled={loadingLands}

                            >

                                {loadingLands ? "Searching..." : "Search Farmland"}

                            </button>

                        </div>

                    </div>


                    {/* FARMLAND SELECT */}

                    <div className="row">

                        <div className="col-md-8">

                            <label className="form-label">

                                Farmland *

                            </label>


                            <select

                                className="form-control"

                                value={selectedLand}

                                onChange={handleLandChange}

                                disabled={loadingLands}

                            >

                                <option value="">

                                    {loadingLands ? "Loading farmlands..." : "Select Farmland"}

                                </option>


                                {lands.map(land => (<option

                                        key={land.id}

                                        value={land.id}

                                    >

                                        {getLandName(land)}

                                    </option>))}

                            </select>

                        </div>

                    </div>


                    {/* FARMLAND INFORMATION */}

                    {selectedLandObject && (<div className="alert alert-info mt-3">

                            <strong>
                                Selected Farmland
                            </strong>

                            <hr/>


                            <div>

                                <strong>
                                    Land:
                                </strong>{" "}

                                {getLandName(selectedLandObject)}

                            </div>


                            <div>

                                <strong>
                                    Land ID:
                                </strong>{" "}

                                {selectedLandObject.land_id || selectedLandObject.id || "-"}

                            </div>

                        </div>)}

                </div>

            </div>


            {/* ==================================================
                STEP 3 — LAND CROP
            ================================================== */}

            <div className="card shadow-sm mb-4">

                <div className="card-header">

                    <strong>
                        3. Select Crop
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row">

                        <div className="col-md-8">

                            <label className="form-label">

                                Crop *

                            </label>


                            <select

                                className="form-control"

                                value={selectedLandCrop}

                                onChange={e => setSelectedLandCrop(e.target.value)}

                                disabled={!selectedLand || loadingLandCrops}

                            >

                                <option value="">

                                    {!selectedLand ? "Select farmland first" : loadingLandCrops ? "Loading crops..." : landCrops.length === 0 ? "No crops found" : "Select Crop"}

                                </option>


                                {landCrops.map(landCrop => (<option

                                        key={landCrop.id}

                                        value={landCrop.id}

                                    >

                                        {getLandCropName(landCrop)}

                                    </option>))}

                            </select>

                        </div>

                    </div>


                    {selectedLandCropObject && (<div className="alert alert-success mt-3">

                            <strong>
                                Selected Crop
                            </strong>

                            <hr/>


                            <div>

                                <strong>
                                    Crop:
                                </strong>{" "}

                                {getLandCropName(selectedLandCropObject)}

                            </div>


                            <div>

                                <strong>
                                    Land Crop ID:
                                </strong>{" "}

                                {selectedLandCropObject.id}

                            </div>

                        </div>)}

                </div>

            </div>


            {/* ==================================================
                STEP 4 — CONFIRM
            ================================================== */}

            <div className="card shadow-sm mb-4">

                <div className="card-header">

                    <strong>
                        4. Confirm Mapping
                    </strong>

                </div>


                <div className="card-body">


                    {/* SUMMARY */}

                    {selectedDevice && selectedLand && selectedLandCrop && (<div className="alert alert-success">

                            <strong>
                                Mapping Summary
                            </strong>

                            <hr/>


                            <div>

                                <strong>
                                    IoT Device:
                                </strong>{" "}

                                {selectedDeviceObject ? selectedDeviceObject.device_id : "-"}

                            </div>


                            <div>

                                <strong>
                                    Device:
                                </strong>{" "}

                                {selectedDeviceObject ? selectedDeviceObject.device_name : "-"}

                            </div>


                            <div>

                                <strong>
                                    Farmland:
                                </strong>{" "}

                                {selectedLandObject ? getLandName(selectedLandObject) : "-"}

                            </div>


                            <div>

                                <strong>
                                    Crop:
                                </strong>{" "}

                                {selectedLandCropObject ? getLandCropName(selectedLandCropObject) : "-"}

                            </div>

                        </div>)}


                    {/* BUTTONS */}

                    <div className="text-center mt-4">

                        <button

                            type="button"

                            className="btn btn-success me-2"

                            onClick={mapDevice}

                            disabled={mapping || !selectedDevice || !selectedLand || !selectedLandCrop}

                        >

                            {mapping ? "Mapping..." : "Map Device to Farmland"}

                        </button>


                        <button

                            type="button"

                            className="btn btn-secondary"

                            onClick={clearMapping}

                            disabled={mapping}

                        >

                            Clear

                        </button>

                    </div>

                </div>

            </div>

        </section>);
}
export default IoTDeviceMapping;
